import { useState } from "react";
import { Camera, CheckCircle, FileText, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "../../utils/api";
import { PhotoLightbox } from "./PhotoLightbox";

const API = `/api`;

function CompletedJobsArchive({ jobs, stages }) {
  const [expandedJob, setExpandedJob] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lightboxPhotos, setLightboxPhotos] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const loadJobDetails = async (jobId) => {
    if (expandedJob === jobId) {
      setExpandedJob(null);
      setJobDetails(null);
      return;
    }
    
    setLoading(true);
    setExpandedJob(jobId);
    try {
      const res = await api.get(`${API}/admin/jobs/${jobId}/full`);
      setJobDetails(res.data.job);
    } catch (err) {
      toast.error("Failed to load job details");
    } finally {
      setLoading(false);
    }
  };

  const getStagePhotos = (stageData) => {
    if (!stageData) return [];
    if (stageData.photos && Array.isArray(stageData.photos)) return stageData.photos;
    if (stageData.photo) return [stageData.photo];
    return [];
  };

  const getAllPhotos = (job) => {
    const photos = [];
    // Pre-work
    (job.pre_work_photos || []).forEach(p => photos.push({ src: p, label: 'Pre-Work' }));
    // Stage photos
    stages?.forEach(stage => {
      const stagePhotos = getStagePhotos(job.stages?.[stage.id]);
      stagePhotos.forEach(p => photos.push({ src: p, label: stage.name }));
    });
    // Post-work
    (job.post_work_photos || []).forEach(p => photos.push({ src: p, label: 'Post-Work' }));
    return photos;
  };

  return (
    <div>
      <h4 className="text-xs tracking-[0.2em] uppercase text-zinc-500 font-body mb-3 flex items-center gap-2">
        <FileText size={12} /> Job Archive ({jobs.length})
      </h4>
      
      {/* Photo Lightbox */}
      {lightboxPhotos && (
        <PhotoLightbox
          photos={lightboxPhotos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxPhotos(null)}
        />
      )}
      
      <div className="space-y-2">
        {jobs.map((job) => (
          <div key={job.id} className="bg-[#09090b] border border-white/5">
            {/* Job Header */}
            <button
              onClick={() => loadJobDetails(job.id)}
              className="w-full p-4 flex items-center justify-between cursor-pointer bg-transparent border-none text-left hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <CheckCircle size={16} className="text-[#22c55e]" />
                <div>
                  <span className="font-heading text-sm uppercase tracking-wide text-white">{job.vehicle}</span>
                  <span className="text-zinc-600 text-xs ml-2">/ {job.customer_name}</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-zinc-700 font-mono text-xs">{job.tracking_code}</span>
                <span className="text-zinc-600 font-mono text-xs">
                  {job.completed_at ? new Date(job.completed_at).toLocaleDateString("en-AU") : ""}
                </span>
              </div>
            </button>
            
            {/* Expanded Details */}
            {expandedJob === job.id && (
              <div className="border-t border-white/5 p-5">
                {loading ? (
                  <div className="text-center py-8">
                    <Loader2 size={24} className="animate-spin text-zinc-600 mx-auto" />
                  </div>
                ) : jobDetails ? (
                  <div className="space-y-6">
                    {/* Customer Info */}
                    <div className="bg-white/5 p-4">
                      <h5 className="text-xs tracking-[0.15em] uppercase text-zinc-500 mb-3">Customer Info</h5>
                      <div className="space-y-2 text-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1">
                          <span className="text-zinc-600 min-w-[60px]">Name:</span>
                          <span className="text-white">{jobDetails.customer_name}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1">
                          <span className="text-zinc-600 min-w-[60px]">Phone:</span>
                          <a href={`tel:${jobDetails.customer_phone}`} className="text-[#3b82f6] no-underline">{jobDetails.customer_phone || 'N/A'}</a>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1">
                          <span className="text-zinc-600 min-w-[60px]">Email:</span>
                          <span className="text-white break-all">{jobDetails.customer_email || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    {/* All Photos */}
                    <div>
                      <h5 className="text-xs tracking-[0.15em] uppercase text-zinc-500 mb-3 flex items-center gap-2">
                        <Camera size={12} /> All Photos ({getAllPhotos(jobDetails).length})
                      </h5>
                      {getAllPhotos(jobDetails).length > 0 ? (
                        <div className="flex gap-2 flex-wrap">
                          {getAllPhotos(jobDetails).map((photo, idx) => (
                            <button
                              key={idx}
                              onClick={() => { setLightboxPhotos(getAllPhotos(jobDetails).map(p => p.src)); setLightboxIndex(idx); }}
                              className="relative w-20 h-20 border border-white/10 hover:border-[#3b82f6]/50 transition-colors cursor-pointer bg-transparent p-0 overflow-hidden group"
                            >
                              <img src={photo.src} alt={photo.label} className="w-full h-full object-cover" />
                              <div className="absolute bottom-0 left-0 right-0 bg-black/70 py-0.5 px-1">
                                <span className="text-[8px] text-white uppercase tracking-wider">{photo.label}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-zinc-700 text-sm">No photos for this job</p>
                      )}
                    </div>

                    {/* Email History */}
                    <div>
                      <h5 className="text-xs tracking-[0.15em] uppercase text-zinc-500 mb-3 flex items-center gap-2">
                        <Mail size={12} /> Email History ({(jobDetails.email_history || []).length})
                      </h5>
                      {(jobDetails.email_history || []).length > 0 ? (
                        <div className="space-y-1">
                          {jobDetails.email_history.map((email, idx) => (
                            <div key={idx} className="bg-white/5 p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <Mail size={14} className="text-[#3b82f6]" />
                                <div>
                                  <span className="text-white text-sm">{email.subject}</span>
                                  <span className="text-zinc-600 text-xs ml-2">→ {email.to}</span>
                                </div>
                              </div>
                              <span className="text-zinc-600 font-mono text-xs">
                                {new Date(email.sent_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-zinc-700 text-sm">No email history recorded</p>
                      )}
                    </div>

                    {/* Job Timeline */}
                    <div>
                      <h5 className="text-xs tracking-[0.15em] uppercase text-zinc-500 mb-3">Job Timeline</h5>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between bg-white/5 p-2 text-sm">
                          <span className="text-zinc-400">Started</span>
                          <span className="text-white font-mono text-xs">{new Date(jobDetails.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>
                        </div>
                        <div className="flex items-center justify-between bg-white/5 p-2 text-sm">
                          <span className="text-zinc-400">Completed</span>
                          <span className="text-[#22c55e] font-mono text-xs">{jobDetails.completed_at ? new Date(jobDetails.completed_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


export { CompletedJobsArchive };
